import axios, { AxiosError, AxiosResponse } from "axios";
import { toast } from "react-toastify";
import { AdminUser, MailRequest, TempRegisterUser, TempRegisterUserActionForm, VerifyOrUpdatedUser } from "../models/admin";
import { AdminAppData } from "../models/adminAppData";
import { AdminCollectionKreditor, KreditorParam } from "../models/adminCollection";
import { Photo } from "../models/common";
import { CustomerCollection, FullCustomerCollection } from "../models/customer";
import { DataErasure, KoDataErasureReport, KoUpReCycle } from "../models/dataErasure";
import { Kobehoved, KoForcastReceived } from "../models/forcastReceived";
import { EmailModalFormValues } from "../models/generalEmail";
import { PaginatedResult } from "../models/pagination";
import {
  ExistingPartner, FullT1aPartnerData, PendingSignUpRequest, SubPartnersKobHoved,
  Ta1AdminKobhoved, Ta1AdminKobhovedPartnerFormValue, TempPartnerSignup
} from "../models/partners";

import {
  PlayerProfile,
  PlayerProfileFormValues,
} from "../models/playerProfile";
import {
  IUseUpdatePasswordFromValue,
  ReferalRegisterSignupForm,
  RefferalSignUpFormValues,
  RegisterSignupForm,
  User,
  UserLoginFormValues,
} from "../models/user";
import { router } from "../router/Routes";
import { store } from "../stores/store";

const sleep = (delay: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, delay);
  });
};

axios.defaults.baseURL = process.env.REACT_APP_API_URL;

axios.interceptors.request.use((config) => {
  const token = store.commonStore.token;
  if (token && config.headers) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

axios.interceptors.response.use(
  async (response) => {
    if (process.env.NODE_ENV === "development") await sleep(1000);
    const pagination = response.headers["pagination"];
    if (pagination) {
      response.data = new PaginatedResult(
        response.data,
        JSON.parse(pagination)
      );
      return response as AxiosResponse<PaginatedResult<any>>;
    }
    return response;
  },
  (error: AxiosError) => {
    const { data, status, config, headers } = error.response as AxiosResponse;
    switch (status) {
      case 400:
        if (config.method === "get" && data.errors.hasOwnProperty("id")) {
          router.navigate("/not-found");
          break;
        }
        if (data.errors) {
          const modalStateErrors = [];
          for (const key in data.errors) {
            if (data.errors[key]) {
              modalStateErrors.push(data.errors[key]);
            }
          }
          throw modalStateErrors.flat();
        } else {
          toast.error(data);
        }
        break;
      case 401:
        if (
          status === 401 &&
          headers["www-authenticate"]?.startsWith(
            'Bearer error="invalid_token"'
          )
        ) {
          store.userStore.logout(true);
          toast.error("Session expired - please login again");
        }
        if (status === 401 && data === 'Invalid email') {
          store.userStore.logout(true);
          toast.error("Invalid Email or approval is pending");
        }
        break;

      // case 404:
      //   router.navigate("/not-found");
      //   break;
      case 500:
        store.commonStore.setServerError(data);
        // router.navigate("/server-error");
        router.navigate("/");
        break;
    }
    return Promise.reject(error);
  }
);

const responseBody = <T>(response: AxiosResponse<T>) => response.data;

const requests = {
  get: <T>(url: string) => axios.get<T>(url).then(responseBody),
  post: <T>(url: string, body: {}) =>
    axios.post<T>(url, body).then(responseBody),
  put: <T>(url: string, body: {}) => axios.put<T>(url, body).then(responseBody),
  del: <T>(url: string) => axios.delete<T>(url).then(responseBody),
  patch: <T>(url: string, body: {}) => axios.patch<T>(url, body).then(responseBody),
};

const Account = {
  current: () => requests.get<User>("/account/currentUser"),
  login: (user: UserLoginFormValues) => requests.post<User>("/account/login", user),

  register: (user: RefferalSignUpFormValues) =>
    requests.post<User>("/account/register", user),

  //First Sign up Request ,  New user Direct Sign Up
  addNewRegisteration: (user: RegisterSignupForm) =>
    requests.post<User>("account/AddNewRegistration", user),

  //After approval sign up Request by admin, need token and email
  addNewSignUpregisterReferal: (user: ReferalRegisterSignupForm) =>
    requests.post<User>("account/RegisterSignUp", user),
  refreshToken: () => requests.post<User>("/account/refreshToken", {}),

  verifyEmail: (token: string, email: string) =>
    requests.post<void>(
      `/account/verifyEmail?token=${token}&email=${email}`,
      {}
    ),
  resendEmailConfirm: (email: string) =>
    requests.get(`/account/resendEmailConfirmationLink?email=${email}`),
  resetPasswordEmail: (email: string): Promise<void> =>
    requests.post(`/account/emailResetPassword`, { email }),
  resetPassword: (
    token: string,
    email: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<void> =>
    requests.post(`/account/resetPassword`, {
      token,
      email,
      newPassword,
      confirmPassword,
    }),
  emailChangeEmail: (currentEmail: string, newEmail: string): Promise<void> =>
    requests.get(
      `/account/emailChangeEmailLink?currentEmail=${currentEmail}&newEmail=${newEmail}`
    ),
  verifyChangeEmail: (
    token: string,
    newEmail: string,
    currentEmail: string
  ): Promise<void> =>
    requests.post(`/account/updateEmail`, {
      token,
      newEmail,
      currentEmail,
    }),
  changePassword: (newPassword: IUseUpdatePasswordFromValue) =>
    requests.post(`/account/changePassword`, newPassword),
  changeUserRole: (newRole: string) => requests.get<string>(`/Account/ChangePartnerRole/${newRole}`),
};

const UserProfile = {
  get: (username: string) =>
    requests.get<PlayerProfile>(`/userProfile/${username}`),
  uploadPhoto: (file: Blob) => {
    let formData = new FormData();
    formData.append("File", file);
    return axios.post<Photo>("/photos", formData, {
      headers: { "Content-type": "multipart/form-data" },
    });
  },
  deletePhoto: () => requests.del(`/photos`),
  updateProfile: (profile: PlayerProfileFormValues) =>
    requests.put(`/userProfile`, profile)
};

const AppData = {
  getLoginRequiredData: () => requests.get<AdminAppData>(`/AppData/loginRequiredData`),
  getAdminAppData: () => requests.get<AdminAppData>(`/AppData/adminAppData`),
  getAdminPartnerKobhoved: () => requests.get<FullT1aPartnerData>('/NewAdmin/GetT1aPartnerKobhoveds'),
}

const Admin = {
  //Not in use
  getAdminUsers: () => requests.get<AdminUser[]>(`/Admin/adminAppUsers`),
  //Not avaiable in API
  verifyOrUpdateUser: (uptUser: VerifyOrUpdatedUser) =>
    requests.post(`/Admin/verifyUser`, uptUser),
  testEmail: (mailRequest: MailRequest) =>
    requests.post(`/Admin/TestEmail`, mailRequest),
  getKobhoveds: (params: URLSearchParams) => axios.get<Ta1AdminKobhoved[]>(`/NewAdmin/GetAdminT1aKobhovedPartners`, { params }),
  // co2List: (params: URLSearchParams) => axios.get<PaginatedResult<Co2[]>>('/Co2', { params })
  // .then(responseBody),
  addNewPartner: (newPartner: Ta1AdminKobhovedPartnerFormValue) =>
    requests.post(`/NewAdmin/AddNewPartner`, newPartner),
  addExistingPartner: (existingPartner: Ta1AdminKobhovedPartnerFormValue) =>
    requests.post(`/NewAdmin/AddPurchaseNoToExistingPartner`, existingPartner),
  getExistingPartner: (userEmail: string) =>
    requests.get<ExistingPartner[]>(`/NewAdmin/GetExistingPartners/${userEmail}`),
  getTempPartner: () =>
    requests.get<TempPartnerSignup[]>('/SignUpPartner'),
  resendTempPartnerEmail: (id: string) =>
    requests.post(`/SignUpPartner/${id}`, {}),
  deleteTempPartner: (id: string) =>
    requests.del(`/SignUpPartner/${id}`),
  //Handle Direct SignUp users
  getTempRegisterUsers: () => requests.get<TempRegisterUser[]>(`/Admin/tempRegisterUsers`),
  verifyTempRegisterUser: (uptUser: TempRegisterUserActionForm) =>
    requests.post(`/Admin/verifyTempRegisterUser`, uptUser),
}

const Partner = {
  getSubPartnerRequests: () => requests.get<SubPartnersKobHoved[]>(`/NewAdmin/GetT1aPartnerKobhoveds`),
  // getPartnerKobhoveds: () => requests.get<Ta1PartnerKobhoved[]>(`/NewAdmin/GetT1aPartnerKobhoveds`),
  getPendingPartnerRequests: () => requests.get<PendingSignUpRequest[]>(`/NewAdmin/GetPendingPartnerSignUp`),
}

const Customer = {
  createCollection: (customerCollection: CustomerCollection) =>
    requests.post<string>(`/Customer/CreateCustomerCollection`, customerCollection),
  sendCustomerCollectionEmail: (formData: FormData) => {
    return axios.post<void>(`/Customer/CustomerCollectionEmail`, formData, {
      headers: { "Content-type": "multipart/form-data" },
    });
  },

  // getCustomerCollectionList: () => requests.get<FullCustomerCollection[]>(`/Customer/GetCustomerCollections`),
  getCustomerCollectionPagedList: (params: URLSearchParams) => axios.get<PaginatedResult<FullCustomerCollection[]>>(`/Customer/GetCustomerCollections`, { params }),
  getCustomerCollection: (koNumber: string) => requests.get<FullCustomerCollection>(`/Customer/${koNumber}/GetCustomerCollection`),
  getCustomerCollectionById: (id: string) => requests.get<FullCustomerCollection>(`/Customer/${id}/GetCustomerCollectionById`),
  getLatestCustomerCollection: () => requests.get<FullCustomerCollection>(`/Customer/GetLatestCustomerCollection`),
  createCollectionWithFile: (formData: FormData) => {
    return axios.post<void>(`/Customer/CreateCustomerCollection`, formData, {
      headers: { "Content-type": "multipart/form-data" },
    });
  },
  // updateCustomerCollection: (customerCollection: FullCustomerCollection) => requests.put<void>(`/Customer/${customerCollection.id}/EditCustomerCollection`, customerCollection),
  updateCustomerCollection: (customerCollection: FullCustomerCollection) => requests.put<string>(`/Customer/${customerCollection.id}/EditCustomerCollection`, customerCollection),
  toggleIsReadyToCollect: (id: string) => requests.put<void>(`/Customer/${id}/ToggleIsReadyToCollect`, {}),
}

const CustomerKoForcastReceived = {
  getKoForcastReceived: (koNumber: string) =>
    requests.get<KoForcastReceived>(`/KoForcastReceived/${koNumber}/GetForcastAndReceived`),
  getKobhovedKoNos: () =>
    requests.get<Kobehoved[]>("/KoForcastReceived/currentUserKos"),
  getKoDataErasureReport: (koNumber: string) =>
    requests.get<DataErasure>(`/DataErasure/${koNumber}/GetDataErasureReport`),
  // exportKoDataErasureReportToExcel: (koNumber: string) => axios.get(`/DataErasure/${koNumber}/GetDataErasureReportInExcel`,
  //   {
  //     headers: {
  //       'Content-Disposition': "attachment; filename=KoDataReport.xlsx; filename*=UTF-8''KoDataReport.xlsx",
  //     },
  //     responseType: 'blob'
  //   }),

}

// current: () => requests.get<User>("/account/currentUser"),
const AdminCollection = {
  // getAdminCollectionList: () => requests.get<FullCustomerCollection[]>(`/AdminCollection/GetAdminCollections`),
  getAdminCollectionPagedList: (params: URLSearchParams) => axios.get<PaginatedResult<FullCustomerCollection[]>>(`/AdminCollection/GetAdminCollections`, { params }),
  getKreditorList: (kreditorParam: KreditorParam) =>
    requests.post<AdminCollectionKreditor[]>(`/AdminCollection/GetKreditors`, kreditorParam),
  addKoNumberToCollection: (id: string, koNumber: string) => requests.put<void>(`/AdminCollection/${id}/AddKoNumberToCollection/${koNumber}`, {}),
  markReadCollectionRead: (id: string) => requests.put<void>(`/AdminCollection/${id}/markReadCollectionRead`, {}),
  deleteCustomerCollection: (id: string) => requests.del<void>(`/AdminCollection/${id}/DeleteCustomerCollection`),
  createCrfForm: (id: string, koNumber: string) => requests.get<void>(`/AdminCollection/${id}/CreateCrfForm/${koNumber}`),
}
const KoOverview = {
  getKoDataErasureReports: () => requests.get<KoDataErasureReport[]>(`/DataErasure/GetDataErasureReports`),
  getKoUpReCycle: () => requests.get<KoUpReCycle>('/DataErasure/GetUpReCycle'),
  downloadKoDataErasureReportToExcel: (koNumber: string) => axios.get(`/DataErasure/${koNumber}/DownloadKoReport`,
    {
      headers: {
        'Content-Disposition': "attachment; filename=LogisticData.xlsx; filename*=UTF-8''KoDataReport.xlsx",
      },
      responseType: 'blob'
    }),
}

const KoReport = {
  downloadRegisterSerialNumbers: (koNumbers: string) => axios.get(`/DataErasure/${koNumbers}/DownloadKoRegistrationReport`,
    {
      headers: {
        'Content-Disposition': "attachment; filename=KoRegistrationSerialNumbers.xlsx; filename*=UTF-8''KoRegistrationSerialNumbers.xlsx",
      },
      responseType: 'blob'
    }),
  downloadUpCycleRecycleReport: (koNumbers: string) => axios.get(`/DataErasure/${koNumbers}/DownloadKoUpCycleRecycleReport`,
    {
      headers: {
        'Content-Disposition': "attachment; filename=KoUpCycleRecycleReport.xlsx; filename*=UTF-8''KoUpCycleRecycleReport.xlsx",
      },
      responseType: 'blob'
    }),
  downloadCo2Report: (koNumbers: string) => axios.get(`/DataErasure/${koNumbers}/DownloadKoCo2Report`,
    {
      headers: {
        'Content-Disposition': "attachment; filename=KoUpCycleRecycleReport.xlsx; filename*=UTF-8''KoCo2Report.xlsx",
      },
      responseType: 'blob'
    }),
}

const GeneralEmail = {
  sendEmail: (emailValues: EmailModalFormValues) =>
    requests.post<string>(`/Customer/CustomerGeneralEmail`, emailValues),
}

const agent = {
  Account,
  UserProfile,
  AppData,
  Admin,
  AdminCollection,
  Customer,
  CustomerKoForcastReceived,
  KoOverview,
  KoReport,
  GeneralEmail,
  Partner,

};
export default agent;
